/* eslint-disable css-modules/no-undef-class */
import classNames from 'classnames';
import { Feature } from 'geojson';
import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useState } from 'react';
import Image from '@/components/atoms/image/image';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import HotelMapDetailsModal from '@/components/modules/hotel-map/hotel-map-details-modal/hotel-map-details-modal';
import Button from '@/components/molecules/button/button';
import Dropdown, {
  DropdownProps,
} from '@/components/molecules/dropdown/dropdown';
import type { MapProps } from '@/components/molecules/map/map';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import {
  extractMapHotelItems,
  extractMapPoiItems,
  hotelMapDetailsCollectionAdapter,
  mapHotelItemsFeatureAdapter,
  mapHotelItemsFeatureCollectionAdapter,
  mapPoiItemsFeatureCollectionAdapter,
} from './hotel-map.adapters';
import styles from './hotel-map.module.scss';

const Map = dynamic(() => import('@/components/molecules/map/map'), {
  loading: () => <SkeletonLoader height="100%" width="100%" />,
  ssr: false,
});

export interface HotelMapProps extends ModuleWrapperProps {
  /** The text to show as a headline */
  headline?: Content.Components['HotelMapModuleDto']['headline'];
  /** The hotel map data to show as markers and pois */
  hotelMap: Content.Components['HotelMapModuleDto']['hotelMap'];
  /** The image to show as a placeholder */
  placeholderImage?: Content.Components['HotelMapModuleDto']['placeholderImage'];
  /** The text to show as a pre headline */
  preHeadline?: Content.Components['HotelMapModuleDto']['preHeadline'];
}

const HotelMap = ({
  headline,
  hotelMap,
  placeholderImage,
  preHeadline,
  ...rest
}: HotelMapProps) => {
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');
  const isTablet = useWindowSize('tablet');
  const { hotelDetails } = usePageDetailsContext();
  const [showMap, setShowMap] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [activeItemId, setActiveItemId] = useState<string>();
  const [selectedPoiFilters, setSelectedPoiFilters] = useState<string[]>([]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const additionalStyles: MapProps['additionalStyles'] = [
    {
      classes: [styles.currentItem],
      icon: 'Location',
      id: String(hotelDetails?.hotelId),
    },
  ];

  const mapHotelItems = useMemo(
    () => extractMapHotelItems(hotelMap.entries),
    [hotelMap],
  );

  const mapPoiItems = useMemo(
    () => extractMapPoiItems(hotelMap.entries),
    [hotelMap],
  );

  const selectedItem = useMemo(
    () =>
      mapHotelItems?.find(
        item => String(item.hotelId) === String(activeItemId),
      ),
    [activeItemId, mapHotelItems],
  );

  const activeItem: Feature | any = useMemo(() => {
    return selectedItem ? mapHotelItemsFeatureAdapter(selectedItem) : undefined;
  }, [selectedItem]);

  const poiFilters = useMemo(() => {
    const items: DropdownProps['items'] = Object.values(
      mapPoiItems.reduce((acc, item) => {
        if (!acc[item.pointOfInterestType]) {
          acc[item.pointOfInterestType] = {
            label: item.filterLabel,
            value: item.pointOfInterestType,
          };
        }

        return acc;
      }, {}) || [],
    );

    return items;
  }, [mapPoiItems]);

  const filteredMapPoiItems = useMemo(
    () =>
      mapPoiItems.filter(item =>
        selectedPoiFilters.includes(item.pointOfInterestType),
      ),
    [mapPoiItems, selectedPoiFilters],
  );

  const formattedMapHotelItems = useMemo(() => {
    return mapHotelItemsFeatureCollectionAdapter(mapHotelItems);
  }, [mapHotelItems]);

  const formattedMapPoiItems = useMemo(() => {
    return mapPoiItemsFeatureCollectionAdapter(filteredMapPoiItems);
  }, [filteredMapPoiItems]);

  // TODO: remove `replaceAllFilters` when dropdown supports multiple selection
  const handleClickPoiFilter = (value: string, replaceAllFilters?: boolean) => {
    setShowMap(true);

    setSelectedPoiFilters(previousValue => {
      const isValueInFilters = previousValue.includes(value);

      if (replaceAllFilters) {
        return isValueInFilters ? [] : [value];
      }

      return isValueInFilters
        ? [...previousValue.filter(item => item !== value)]
        : [...previousValue, value];
    });
  };

  const handleClickMap = (feature: Feature | any) => {
    setActiveItemId(prevId => {
      if (prevId === feature.properties.id) {
        return undefined;
      } else {
        return feature.properties.id;
      }
    });
  };

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      {(preHeadline || headline) && (
        <Container>
          <Row>
            <Column className={styles.textColumn}>
              {preHeadline && (
                <Typography
                  className={styles.preHeadline}
                  tagStyle="labelMedium"
                >
                  {preHeadline}
                </Typography>
              )}

              {headline && (
                <Typography
                  className={styles.headline}
                  tagStyle="displayMedium"
                >
                  {headline}
                </Typography>
              )}
            </Column>
          </Row>
        </Container>
      )}

      <Container>
        <Row>
          <Column className={styles.mapColumn}>
            {poiFilters.length > 0 && (
              <div className={styles.poiContainer}>
                {isLaptop ? (
                  <React.Fragment>
                    <Typography
                      className={styles.poiHeader}
                      tagStyle="bodyMediumBold"
                    >
                      {`${t('items.maps.mapFilters')}`}
                    </Typography>

                    <div className={styles.filterGroup}>
                      {poiFilters.map((item, index) => (
                        <button
                          key={index}
                          className={classNames(styles.filter, {
                            [styles.isActive]: selectedPoiFilters.includes(
                              item.value,
                            ),
                          })}
                          onClick={() => handleClickPoiFilter(item.value)}
                        >
                          <Typography tagStyle="bodyMediumBold">
                            {item.label}
                          </Typography>
                        </button>
                      ))}
                    </div>
                  </React.Fragment>
                ) : (
                  <Dropdown<string>
                    className={styles.dropdown}
                    items={poiFilters}
                    label={t('items.maps.mapFilters')}
                    onChange={value => handleClickPoiFilter(value, true)}
                    value={selectedPoiFilters[0]}
                  />
                )}
              </div>
            )}

            {hotelMap && isMounted && (
              <div className={styles.mapWrapper}>
                {!placeholderImage || showMap ? (
                  <Map
                    activeItem={activeItem}
                    additionalStyles={additionalStyles}
                    controlPosition={isTablet ? 'bottom-right' : undefined}
                    items={{
                      markers: formattedMapHotelItems,
                      pois: formattedMapPoiItems,
                    }}
                    onClickMarker={handleClickMap}
                  />
                ) : (
                  <div className={styles.placeholder}>
                    <Image
                      alt={placeholderImage.altText}
                      className={styles.placeholderImage}
                      fill
                      height={459}
                      src={placeholderImage.url}
                      srcSet={{
                        desktop: {
                          height: 756,
                          width: 1344,
                        },
                        laptop: {
                          height: 532,
                          width: 945,
                        },
                        tablet: {
                          height: 517,
                          width: 720,
                        },
                      }}
                      unoptimized
                      width={328}
                    />

                    <Button
                      className={styles.placeholderButton}
                      onClick={() => setShowMap(true)}
                      text={t('items.common.words.showMap')}
                    />
                  </div>
                )}
              </div>
            )}
          </Column>
        </Row>
      </Container>

      <HotelMapDetailsModal
        aspectRatio={isLaptop ? 'fourByThree' : 'sixteenByNine'}
        {...hotelMapDetailsCollectionAdapter(selectedItem)}
        isOpen={!!activeItemId}
        onClose={() => setActiveItemId(undefined)}
      />
    </ModuleWrapper>
  );
};

HotelMap.displayName = 'HotelMap';

export default HotelMap;
